/**
 * MqlReconciliation — W55 "MQL Reconciliation · Claimed vs Accepted"
 * W57: rewired to query mql_events via /api/marketing/funnel instead of accounts.status proxy.
 *
 * Table: Claimed by marketing / Accepted by BDR / Gap-disputed (with %).
 * Subtitle: "Reconciliation review every Friday with BDR lead."
 *
 * Data from /api/marketing/funnel (totals field — all-time):
 *  - Claimed:  mql_events stage=MQL  (marketing's MQL claim)
 *  - Accepted: mql_events stage=SAL  (BDR confirmed / SAL)
 *  - Gap:      difference = disputed / under review
 *
 * Tenant 1 only.
 */

import { useEffect, useState } from "react";
import { api, getCurrentTenant } from "@/lib/session";

const TENANT_ID = 1;

interface ReconcRow {
  label: string;
  claimed: number;
  accepted: number;
  gap: number;
  gapPct: number;
}

export function MqlReconciliation() {
  const [data, setData] = useState<ReconcRow | null>(null);
  const [loading, setLoading] = useState(true);
  const tenant = getCurrentTenant();

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    api<{
      totals: { MQL: number | null; SAL: number | null; SQL: number | null; OPPORTUNITY: number | null };
    }>("/api/marketing/funnel")
      .then((resp) => {
        if (cancelled) return;

        const claimed = resp?.totals?.MQL ?? 0;
        const accepted = resp?.totals?.SAL ?? 0;
        const gap = Math.max(0, claimed - accepted);
        const gapPct = claimed > 0 ? Math.round((gap / claimed) * 100) : 0;

        setData({ label: "All-time pipeline", claimed, accepted, gap, gapPct });
        setLoading(false);
      })
      .catch(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [tenant?.tenantId]);

  if (loading) {
    return (
      <div className="rounded-lg border border-neutral-200 bg-white p-4 animate-pulse">
        <div className="h-4 w-48 bg-neutral-100 rounded mb-4" />
        <div className="h-28 w-full bg-neutral-100 rounded" />
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-neutral-200 bg-white p-4">
      <div className="mb-0.5 text-[10px] font-bold uppercase tracking-widest text-neutral-500">
        MQL Reconciliation &middot; Claimed vs Accepted
      </div>
      <p className="mb-4 text-[11px] text-neutral-400">
        Reconciliation review every Friday with BDR lead.
      </p>

      <table className="w-full text-sm">
        <thead className="text-left text-[11px] text-neutral-400">
          <tr>
            <th className="pb-2 font-medium">Metric</th>
            <th className="pb-2 text-right font-medium">Count</th>
            <th className="pb-2 text-right font-medium">%</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-neutral-100">
          <tr>
            <td className="py-2 text-neutral-700">Claimed by marketing as MQL</td>
            <td className="py-2 text-right tabular-nums font-semibold">{data?.claimed ?? 0}</td>
            <td className="py-2 text-right tabular-nums text-neutral-400">100%</td>
          </tr>
          <tr>
            <td className="py-2 text-neutral-700">Accepted by BDR as qualified</td>
            <td className="py-2 text-right tabular-nums font-semibold" style={{ color: "#15803D" }}>
              {data?.accepted ?? 0}
            </td>
            <td className="py-2 text-right tabular-nums" style={{ color: "#15803D" }}>
              {data?.claimed ? Math.round(((data.accepted) / data.claimed) * 100) : 0}%
            </td>
          </tr>
          <tr>
            <td className="py-2 text-neutral-700">
              Gap &middot; disputed
            </td>
            <td
              className="py-2 text-right tabular-nums font-semibold"
              style={{ color: (data?.gap ?? 0) > 0 ? "#DC2626" : "#15803D" }}
            >
              {data?.gap ?? 0}
            </td>
            <td
              className="py-2 text-right tabular-nums"
              style={{ color: (data?.gap ?? 0) > 0 ? "#DC2626" : "#15803D" }}
            >
              {data?.gapPct ?? 0}%
            </td>
          </tr>
        </tbody>
      </table>

      {(data?.gap ?? 0) > 0 && (
        <p className="mt-3 text-[11px] text-neutral-500 border-t border-neutral-100 pt-3">
          {data!.gap} MQLs claimed by marketing have not been accepted by BDR.
          Review on Friday with BDR lead to resolve disputes.
        </p>
      )}
    </div>
  );
}
